import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import Switch from "react-switch";
import DatePicker from "react-datepicker";
import Select from 'react-select';
import { FaRegCalendar } from 'react-icons/fa';
import { useAppContext } from '../context/AppContext';
import Swal from 'sweetalert2';

import "react-datepicker/dist/react-datepicker.css";

const ChooseModeContainer = styled.div`
  --spacing-small: 0.5rem;
  --spacing-medium: 1rem;
  --spacing-large: 1.5rem;
  --font-size-small: 0.875rem;
  --font-size-medium: 1rem;
  --font-size-large: 1.125rem;
  --switch-width: 5.625rem;
  --switch-height: 1.875rem;
  --switch-handle-size: 1.25rem;

  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${props => props.theme.colors.background};
  width: 100%; 
  overflow: visible;
  flex-wrap: wrap; /* Allow wrapping within the control group */
  z-index: 1001;
  padding-bottom: var(--spacing-medium);
`;

const ControlGroup = styled.div`
  display: flex;
  justify-content: space-between;
  flex: 1; /* Allow the ControlGroup to grow and fill horizontal space */
  flex-wrap: wrap; /* Allow wrapping within the control group */
  align-items: center;
  padding-bottom: var(--spacing-large);
  gap: var(--spacing-medium); /* Add spacing between flex items */
`;

const ToggleModeDiv = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const ModeTextDiv = styled.div`
  font-size: var(--font-size-medium);
  font-family: 'Montserrat', sans-serif;
  text-align: center;
  color: ${props => props.theme.colors.primary};
  font-weight: bold;
  width: 6.0rem;
  flex-shrink: 0;
`;

const DatePickerWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  height: 1.5rem;
  z-index: 1001;
  margin-left: var(--spacing-large);
`;

const StyledDatePicker = styled(DatePicker)`
  border-radius: 0.9375rem;
  padding: var(--spacing-small);
  font-size: var(--font-size-small);
  font-family: 'Montserrat', sans-serif;
  outline: none;
  border: none;
  color: ${props => props.theme.colors.text};
  width: 5.0rem;
  background-color: rgb(71, 71, 71);
`;

const DropdownWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: var(--spacing-small);
  margin-right: var(--spacing-small);
  flex-shrink: 0;
`;

const DropdownLabel = styled.label`
  font-weight: bold;
  font-size: var(--font-size-small);
  margin-right: var(--spacing-small);
  color: ${props => props.theme.colors.text};
`;

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: 'rgb(71, 71, 71)',
    borderColor: 'rgb(71, 71, 71)',
    borderRadius: '0.9375rem',
    padding: '0.0',
    fontSize: '0.95rem',
    fontFamily: 'Montserrat, sans-serif',
    color: 'white',
    midWidth: '8.0rem',
    width: 'auto',
    height: '2.0rem',
    cursor: 'pointer',
    alignItems: 'center', // Center vertically
    '&:hover': {
      borderColor: state.isFocused ? '#FDBA31' : 'rgb(71, 71, 71)',
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: 'white',
    width: '100%',
    margin: '0', // Remove default margins
    padding: '0.0rem',
    textAlign: 'center', // Center text horizontally
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#FDBA31' : 'rgb(71, 71, 71s)',
    color: state.isSelected ? 'black' : 'white',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#FDBA31',
      color: 'black',
    },
  }),
  menu: (provided) => ({
    ...provided,
    width: 'auto',
    backgroundColor: 'rgb(71, 71, 71)',
    zIndex: 9999,
    marginTop: '0.3125rem',
  }),
  menuList: (provided) => ({
    ...provided,
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '0.5rem',
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgb(51, 51, 51)',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#FDBA31',
      borderRadius: '0.25rem',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 0.375rem',
    height: '1.5rem',
    alignItems: 'center', // Center vertically
  }),
  input: (provided) => ({
    ...provided,
    color: 'white',
    margin: '0',
    padding: '0',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '2.0rem', // Ensure the indicators container matches the control height
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: 'white',
    '&:hover': {
      color: '#FDBA31',
    },
  }),
};

const ChooseMode = () => {
  const {
    user,
    captureModeIsActive,
    setCaptureModeIsActive,
    groupAnalysisMode,
    setGroupAnalysisMode,
    startDate,
    setStartDate,
    currentCampus,
    setCurrentCampus,
    currentProgram,
    setCurrentProgram,
    currentGender,
    setCurrentGender,
    currentEvalCount,
    setCurrentEvalCount,
    currentDataType,
    setCurrentDataType,
    campusList,
    updateAthleteList,
    athlete,
    loadInputValuesObject,
    currentAthleteEvaluations,
    currentAthleteSessionIndex
  } = useAppContext();

  const handleCaptureModeChange = useCallback(() => {
    
    const newCaptureMode = !captureModeIsActive;

    setCaptureModeIsActive(newCaptureMode);

    loadInputValuesObject(newCaptureMode, currentAthleteEvaluations, currentAthleteSessionIndex);

    if (newCaptureMode) {
      // When entering capture mode, reset to blank evaluation
      setGroupAnalysisMode(false);
    }

  }, [captureModeIsActive, setCaptureModeIsActive, loadInputValuesObject, currentAthleteEvaluations, currentAthleteSessionIndex, setGroupAnalysisMode ]);

  const handleGroupAnalysisModeChange = useCallback(() => {
    setGroupAnalysisMode(!groupAnalysisMode);
  }, [groupAnalysisMode, setGroupAnalysisMode]);

  const handleDateChangeRaw = useCallback((e) => {
    const typedDate = new Date(e.target.value);
    const currentDate = new Date();
    const minDate = new Date('2010-01-01');

    if (typedDate > currentDate || typedDate < minDate) {
      setStartDate(currentDate);
      Swal.fire({
        title: 'Invalid Date',
        text: 'Dates must be between 2010 and today.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  }, [setStartDate]);

  //const getDayClassName = useCallback(() => "custom-calendar-day", []);

  const handleCampusChange = useCallback((selectedOption) => {
    setCurrentCampus(selectedOption.value);
    updateAthleteList(selectedOption.value, currentProgram, currentGender, currentEvalCount, currentDataType, athlete);
  }, [setCurrentCampus, updateAthleteList, currentProgram, currentGender, currentEvalCount, currentDataType, athlete]);

  const handleProgramChange = useCallback((selectedOption) => {
    setCurrentProgram(selectedOption.value);
    updateAthleteList(currentCampus, selectedOption.value, currentGender, currentEvalCount, currentDataType, athlete);
  }, [setCurrentProgram, updateAthleteList, currentCampus, currentGender, currentEvalCount, currentDataType, athlete]);

  const handleGenderChange = useCallback((selectedOption) => {
    setCurrentGender(selectedOption.value);
    updateAthleteList(currentCampus, currentProgram, selectedOption.value, currentEvalCount, currentDataType, athlete);
  }, [setCurrentGender, updateAthleteList, currentCampus, currentProgram, currentEvalCount, currentDataType, athlete]);

  const handleEvalCountChange = useCallback((selectedOption) => {
    setCurrentEvalCount(selectedOption.value);
    updateAthleteList(currentCampus, currentProgram, currentGender, selectedOption.value, currentDataType, athlete);
  }, [setCurrentEvalCount, updateAthleteList, currentCampus, currentProgram, currentGender, currentDataType, athlete]);

  const handleDataTypeChange = useCallback((selectedOption) => {
    setCurrentDataType(selectedOption.value);
    updateAthleteList(currentCampus, currentProgram, currentGender, currentEvalCount, selectedOption.value, athlete);
  }, [setCurrentDataType, updateAthleteList, currentCampus, currentProgram, currentGender, currentEvalCount, athlete]);

  const campusOptions = useMemo(() => user?.role === 'admin' ? 
    [{ value: 'All', label: 'All' },
    ...campusList.map(campus => ({ value: campus, label: campus }))]
    :
    campusList.map(campus => ({ value: campus, label: campus }))
  , [campusList, user]);

  const programOptions = useMemo(() => [
    { value: 'All', label: 'All' },
    { value: 'Student', label: 'Student' },
    { value: 'Forever', label: 'Forever' },
    { value: 'Next Level', label: 'Next Level' },
    { value: 'Rebuild', label: 'Rebuild' },
    { value: 'Prebuild', label: 'Prebuild' }
  ], []);

  const genderOptions = useMemo(() => [
    { value: 'All', label: 'All' },
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' }
  ], []);

  const evalCountOptions = useMemo(() => [
    { value: 'Any', label: 'Any' },
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3+', label: '3+' }
  ], []);

  const dataTypeOptions = useMemo(() => [
    { value: 'Any', label: 'Any' },
    { value: 'Eval', label: 'Eval' },
    { value: 'Training', label: 'Training' }
  ], []);

  return (
    <ChooseModeContainer>
      <ControlGroup style={{ maxWidth: '25%', justifyContent: 'left' }}>
        <ToggleModeDiv>
          <ModeTextDiv>{captureModeIsActive ? 'CAPTURE' : 'ANALYZE'}</ModeTextDiv>
          <Switch
            handleDiameter={24}
            height={20}
            width={90}
            offColor="#EFEFEF"
            onColor="#F2AF36"
            onHandleColor="#EFEFEF"
            offHandleColor="#F2AF36"
            uncheckedIcon={false}
            checkedIcon={false}
            boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
            activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
            onChange={handleCaptureModeChange}
            checked={captureModeIsActive}
          />
        </ToggleModeDiv>

        {!captureModeIsActive && (          
          <ToggleModeDiv>
            <ModeTextDiv>{groupAnalysisMode ? 'GROUP' : 'EVAL'}</ModeTextDiv>
            <Switch
              handleDiameter={24}
              height={20}
              width={90}
              offColor="#EFEFEF"
              onColor="#F2AF36"
              onHandleColor="#EFEFEF"
              offHandleColor="#F2AF36"
              uncheckedIcon={false}
              checkedIcon={false}
              boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
              activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
              onChange={handleGroupAnalysisModeChange}
              checked={groupAnalysisMode}
            />
          </ToggleModeDiv>
        )}

        {captureModeIsActive && (
          <DatePickerWrapper>
            <DropdownLabel>Date:</DropdownLabel>
            <StyledDatePicker
              selected={startDate}
              onChange={setStartDate}
              onChangeRaw={handleDateChangeRaw}
              disabled={!captureModeIsActive}
            />
            <FaRegCalendar style={{ marginLeft: '-1.875rem', color: '#F2AF36' }} />
          </DatePickerWrapper>
        )}
      </ControlGroup>
      
      <ControlGroup style={{ maxWidth: '75%', justifyContent: 'right'}}>
        <DropdownWrapper>
          <DropdownLabel>Campus:</DropdownLabel>
          <Select
            options={campusOptions}
            value={campusOptions.find(option => option.value === currentCampus)}
            onChange={handleCampusChange}
            styles={customSelectStyles}
          />
        </DropdownWrapper>
        
        <DropdownWrapper>
          <DropdownLabel>Program:</DropdownLabel>
          <Select
            options={programOptions}
            value={programOptions.find(option => option.value === currentProgram)}
            onChange={handleProgramChange}
            styles={customSelectStyles}
          />
        </DropdownWrapper>
        
        <DropdownWrapper>
          <DropdownLabel>Gender:</DropdownLabel>
          <Select
            options={genderOptions}
            value={genderOptions.find(option => option.value === currentGender)}
            onChange={handleGenderChange}
            styles={customSelectStyles}
          />
        </DropdownWrapper>
        
        <DropdownWrapper>
          <DropdownLabel>#Evals:</DropdownLabel>
          <Select
            options={evalCountOptions}
            value={evalCountOptions.find(option => option.value === currentEvalCount)}
            onChange={handleEvalCountChange}
            styles={customSelectStyles}
          />
        </DropdownWrapper>
        
        <DropdownWrapper>
          <DropdownLabel>Data:</DropdownLabel>
          <Select
            options={dataTypeOptions}
            value={dataTypeOptions.find(option => option.value === currentDataType)}
            onChange={handleDataTypeChange}
            styles={customSelectStyles}
          />
        </DropdownWrapper>
      </ControlGroup>
    </ChooseModeContainer>
  );
};

export default ChooseMode;